import { computed, inject, Signal } from '@angular/core';

import { SiteId } from 'app/site-detail/models/Site';
import type { Owner } from 'app/site-owner/models/owner.model';

import { VIDEO_CALL_STATUS, type VideoCall } from '../models/video-call';
import { VideoCallSessionStore } from '../video-call-session.store';
import { VideoCallsStore } from '../video-calls.store';

export type VideoCallButtonOwner = Owner | null;

export type VideoCallButtonStateView =
    | { action: 'APPROVE_SEND_INVITATION' | 'NONE'; label: string; owner: VideoCallButtonOwner }
    | { action: 'RESEND_INVITATION'; label: string; videoCall: VideoCall; owner: VideoCallButtonOwner }
    | { action: 'JOIN_VIDEO_CALL'; label: string; videoCall: VideoCall; owner: VideoCallButtonOwner }
    | { action: 'STOP_AND_JOIN_VIDEO_CALL'; label: string; videoCall: VideoCall; owner: VideoCallButtonOwner };

export function useVideoCallButtonState(siteId: Signal<SiteId | undefined>, owner: Signal<VideoCallButtonOwner>) {
    const videoCalls = inject(VideoCallsStore);
    const session = inject(VideoCallSessionStore);

    const vm = computed(() => {
        const id = siteId();
        if (!id) return undefined;
        return buildVideoCallButtonState(videoCalls.callFor(id), session.siteId(), owner());
    });

    const action = computed(() => vm()?.action);
    const label = computed(() => vm()?.label);
    const unavailable = computed(() => action() === 'NONE');
    const visible = computed(() => vm()?.owner != null);

    return {
        vm,
        action,
        label,
        unavailable,
        visible,
    };
}

function buildVideoCallButtonState(
    videoCall: VideoCall | undefined,
    currentVideoCallSiteId: SiteId | null,
    owner: VideoCallButtonOwner,
): VideoCallButtonStateView {
    if (videoCall?.status === VIDEO_CALL_STATUS.IN_PROGRESS)
        return { action: 'NONE', label: 'VIDEO_CALL_IN_PROGRESS', owner };

    if (videoCall?.status === VIDEO_CALL_STATUS.AVAILABLE) {
        if (currentVideoCallSiteId !== null && currentVideoCallSiteId !== videoCall.siteId)
            return { action: 'STOP_AND_JOIN_VIDEO_CALL', label: 'VIDEO_CALL_END_AND_JOIN_AVAILABLE', videoCall, owner };

        return { action: 'JOIN_VIDEO_CALL', label: 'VIDEO_CALL_AVAILABLE_TO_JOIN', videoCall, owner };
    }

    if (videoCall?.status === VIDEO_CALL_STATUS.INVITATION_SENT)
        return { action: 'RESEND_INVITATION', label: 'VIDEO_CALL_RESEND_INVITATION', videoCall, owner };

    return { action: 'APPROVE_SEND_INVITATION', label: 'VIDEO_CALL_SEND_INVITATION_LABEL', owner };
}
